/**
 * Self-hosted fonts (no font CDN: docs/research/05 §5.5). First load stays near 114 KB:
 * Atkinson Hyperlegible Next (body, ~34 KB latin), Fraunces wght+SOFT (display, ~62 KB)
 * and Atkinson Hyperlegible Mono (codes, ~18 KB). Other subsets load only when a glyph
 * in their unicode-range appears. Shantell Sans (notes) and Lexend (Wide reading style,
 * Vietnamese) load on demand.
 */
import '@fontsource-variable/atkinson-hyperlegible-next/wght.css'
import '@fontsource-variable/atkinson-hyperlegible-mono/wght.css'
import frauncesLatin from '@fontsource-variable/fraunces/files/fraunces-latin-soft-normal.woff2'
import frauncesLatinExt from '@fontsource-variable/fraunces/files/fraunces-latin-ext-soft-normal.woff2'

const FONT_STYLE_ID = 'galley-fonts'

/*
 * Fraunces is declared here rather than through its package CSS so it can use
 * `font-display: fallback`: on slow school Wi-Fi a heading keeps its fallback serif instead
 * of visibly swapping seconds later. (The wordmark is outlined SVG and never waits for it.)
 */
const DISPLAY_FACES = `
@font-face {
  font-family: 'Fraunces Variable';
  font-style: normal;
  font-display: fallback;
  font-weight: 100 900;
  src: url(${frauncesLatin}) format('woff2-variations');
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
@font-face {
  font-family: 'Fraunces Variable';
  font-style: normal;
  font-display: fallback;
  font-weight: 100 900;
  src: url(${frauncesLatinExt}) format('woff2-variations');
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}`

/** Declare the display face once. Idempotent. */
export function installDisplayFont(doc: Document = document): void {
  if (doc.getElementById(FONT_STYLE_ID)) return
  const el = doc.createElement('style')
  el.id = FONT_STYLE_ID
  el.textContent = DISPLAY_FACES
  doc.head.append(el)
}

let noteFont: Promise<void> | null = null
let wideFont: Promise<void> | null = null

// Fonts are an enhancement: a failed chunk load falls back to the body face and may retry later.

/** Shantell Sans for sticky notes; call when the first note is about to show. */
export function loadNoteFont(): Promise<void> {
  noteFont ??= import('@fontsource-variable/shantell-sans/wght.css').then(
    () => undefined,
    () => {
      noteFont = null
    },
  )
  return noteFont
}

/** Lexend for the Wide reading style and for Vietnamese student work (`:lang(vi)`). */
export function loadWideFont(): Promise<void> {
  wideFont ??= import('@fontsource-variable/lexend/wght.css').then(
    () => undefined,
    () => {
      wideFont = null
    },
  )
  return wideFont
}
